/**
 * Cover Letter Template Generator
 *
 * Creates personalized, professional cover letter templates for career transitions:
 * - Opening paragraphs that hook the reader
 * - Body paragraphs highlighting transferable skills
 * - Closing paragraphs with clear calls to action
 * - Segment-specific variations (veteran, career changer, etc.)
 */

import { CareerPath } from './careerPaths';

export interface CoverLetter {
  template: CoverLetterTemplate;
  paragraphs: CoverLetterParagraph[];
  powerPhrases: string[];
  avoidPhrases: string[];
  tips: CoverLetterTip[];
  checklist: string[];
}

export interface CoverLetterTemplate {
  greeting: string;
  opening: string;
  body: string[];
  closing: string;
  signature: string;
}

export interface CoverLetterParagraph {
  id: string;
  type: 'opening' | 'body' | 'skills' | 'closing';
  title: string;
  content: string;
  tips: string;
}

export interface CoverLetterTip {
  category: 'do' | 'dont' | 'format';
  text: string;
}

// Segment-specific opening hooks
const SEGMENT_OPENINGS: Record<string, string> = {
  veteran: `As a [BRANCH] veteran with [X years] of service, I've developed skills in leadership, problem-solving, and performing under pressure that directly translate to the ${'{TITLE}'} role at [COMPANY]. I'm excited to bring this discipline and dedication to your team.`,

  'career-changer': `While my background is in [PREVIOUS FIELD], I've spent the past [TIME PERIOD] developing skills specifically for ${'{TITLE}'} roles. This career transition is deliberate—I've discovered that my passion lies in ${'{CATEGORY}'}, and I'm committed to building a career here.`,

  returning: `After [REASON FOR BREAK], I'm returning to the workforce with renewed focus and energy. The ${'{TITLE}'} position at [COMPANY] aligns perfectly with my experience in [RELEVANT BACKGROUND] and my goal of contributing meaningfully to a team again.`,

  student: `As a recent graduate with a degree in [FIELD] and hands-on experience through [INTERNSHIPS/PROJECTS/COURSEWORK], I'm eager to begin my career as a ${'{TITLE}'}. What I may lack in years of experience, I make up for with fresh perspectives and enthusiasm to learn.`,

  general: `I'm writing to express my strong interest in the ${'{TITLE}'} position at [COMPANY]. With my background in [RELEVANT FIELD] and proven ability to [KEY SKILL], I'm confident I can contribute value to your team from day one.`,
};

// Career-specific skill paragraphs
const CAREER_SKILL_PARAGRAPHS: Record<string, string[]> = {
  web_developer: [
    "My technical foundation includes HTML, CSS, JavaScript, and React—skills I've applied in [X] portfolio projects that demonstrate real-world problem-solving. Beyond coding, I understand the importance of writing clean, maintainable code and collaborating effectively with cross-functional teams.",
    "What sets me apart is my approach to learning: I don't just complete tutorials, I build. My portfolio at [PORTFOLIO URL] showcases projects including [PROJECT 1] and [PROJECT 2], each solving a genuine user problem. I'm particularly proud of [SPECIFIC ACHIEVEMENT].",
  ],

  customer_success_specialist: [
    "Throughout my career, I've consistently prioritized client satisfaction, resulting in [METRIC: retention rate, satisfaction scores, or testimonials]. I understand that customer success isn't just about solving problems—it's about building relationships that drive mutual growth.",
    "My experience managing [NUMBER] client relationships simultaneously has honed my ability to prioritize, communicate proactively, and anticipate needs before they become issues. I'm particularly skilled at [SPECIFIC CS SKILL: onboarding, renewal conversations, upselling, etc.].",
  ],

  project_manager: [
    "I've led projects with budgets up to [$X] and teams of [X] people, consistently delivering on time and within scope. My approach combines rigorous planning with the flexibility to adapt when circumstances change—because they always do.",
    "Beyond methodology, I believe project management is fundamentally about people. I excel at aligning stakeholders, removing blockers for my team, and maintaining transparency throughout the project lifecycle. My [CERTIFICATION: PMP, Scrum, etc.] certification provides the framework, but my results speak louder.",
  ],

  electrician: [
    "I'm seeking an apprenticeship to build my skills in [COMMERCIAL/RESIDENTIAL/INDUSTRIAL] electrical work. I've completed [PRE-APPRENTICESHIP TRAINING/OSHA CERTIFICATION] and understand that becoming a skilled electrician requires years of dedicated learning—I'm ready for that commitment.",
    "What I bring to your team: reliability, attention to detail, and respect for safety protocols. I'm physically fit, mechanically inclined, and genuinely passionate about learning the trade from experienced professionals.",
  ],

  graphic_designer: [
    "My design work spans [BRANDING/UI/UX/PRINT/DIGITAL], all united by a core belief: great design should work as hard as it looks. My portfolio at [URL] demonstrates my range and my process—from understanding the brief to delivering solutions that exceed expectations.",
    "I'm proficient in [TOOLS: Figma, Adobe Creative Suite, etc.] and stay current with design trends while maintaining timeless principles. What excites me most about [COMPANY] is [SPECIFIC REASON: their design aesthetic, clients, mission, etc.].",
  ],

  medical_assistant: [
    "I recently completed my medical assistant certification at [SCHOOL], including [X] hours of clinical externship experience at [FACILITY TYPE]. I'm trained in patient intake, vital signs, phlebotomy, EHR documentation, and patient education.",
    "What drives me in healthcare is the opportunity to directly impact patient wellbeing. During my externship, I worked with [X] patients and received positive feedback for my calm demeanor and attention to detail in clinical documentation.",
  ],

  personal_trainer: [
    "As a [CERTIFICATION: NASM/ACE/ISSA] certified personal trainer, I'm passionate about helping clients transform their health and confidence. My training philosophy centers on sustainable habits rather than quick fixes—I believe in building fitness for life.",
    "I've worked with clients across [AGE RANGES/FITNESS LEVELS/SPECIALIZATIONS], helping them achieve goals from weight loss to competition prep. My approach is personalized, encouraging, and grounded in exercise science.",
  ],
};

// Career-specific closing paragraphs
const CAREER_CLOSINGS: Record<string, string> = {
  web_developer: "I'd welcome the opportunity to discuss how my technical skills and enthusiasm for building great user experiences can contribute to [COMPANY]. I'm available for an interview at your convenience and can be reached at [PHONE] or [EMAIL]. Thank you for considering my application.",

  customer_success_specialist: "I'd love to bring my passion for client relationships to [COMPANY] and contribute to your customers' success. I'm available to discuss how my experience aligns with your team's needs. Thank you for your consideration—I look forward to the possibility of joining your CS team.",

  project_manager: "I'm confident that my project management experience and leadership approach would be valuable to [COMPANY]. I'd welcome the opportunity to discuss how I can help drive successful project outcomes for your organization. Thank you for your time and consideration.",

  electrician: "I'm eager to begin my apprenticeship and commit to learning the electrical trade. I'm reliable, safety-conscious, and ready to work hard. Thank you for considering my application—I'd be honored to learn from your team.",

  graphic_designer: "I'd love to bring my creative skills to [COMPANY] and contribute to work that [COMPANY'S MISSION/AESTHETIC]. My portfolio is available at [URL], and I'm happy to share additional samples relevant to your needs. Thank you for your consideration.",

  medical_assistant: "I'm excited about the opportunity to contribute to patient care at [COMPANY/CLINIC]. I'm available to start [TIMEFRAME] and can provide references from my externship supervisor. Thank you for considering my application.",

  personal_trainer: "I'm passionate about helping clients achieve their fitness goals and would be thrilled to bring that energy to [GYM/STUDIO]. I'm available to discuss how I can contribute to your team and serve your members. Thank you for your consideration.",
};

// Power phrases to include
const POWER_PHRASES = [
  "Consistently delivered...",
  "Proactively identified and resolved...",
  "Collaborated with cross-functional teams to...",
  "Achieved [X%] improvement in...",
  "Successfully managed...",
  "Demonstrated ability to...",
  "Contributed to...",
  "Recognized for...",
  "Passionate about...",
  "Eager to apply my skills in...",
];

// Phrases to avoid (with explanations)
const AVOID_PHRASES = [
  '"To whom it may concern" - Use specific names or "Hiring Manager"',
  '"I think I would be good at..." - Sound confident, not hopeful',
  '"I need this job because..." - Focus on what you offer, not what you need',
  '"As you can see from my resume..." - Don\'t be redundant',
  '"I\'m a perfectionist" - Cliché; show, don\'t tell',
  '"I\'m a hard worker" - Too generic; provide specific examples',
  '"I\'m a team player" - Show it with an example instead',
  '"References available upon request" - Outdated phrase',
  '"I\'ve always wanted to work at..." - Focus on current value, not dreams',
];

// Cover letter tips
const UNIVERSAL_TIPS: CoverLetterTip[] = [
  { category: 'do', text: "Address the hiring manager by name when possible (check LinkedIn or call the company)" },
  { category: 'do', text: "Mirror language from the job posting—if they say 'client success,' you say 'client success,' not 'customer satisfaction'" },
  { category: 'do', text: "Include specific metrics and achievements whenever possible (numbers stand out)" },
  { category: 'do', text: "Show you've researched the company by mentioning something specific about them" },
  { category: 'do', text: "Keep it to one page—hiring managers spend 7 seconds on initial review" },
  { category: 'dont', text: "Don't repeat your resume—expand on it with context and personality" },
  { category: 'dont', text: "Don't be generic—a cover letter that could apply to any company helps no one" },
  { category: 'dont', text: "Don't apologize for what you lack—focus on what you bring" },
  { category: 'dont', text: "Don't use a tiny font to fit more content—white space is professional" },
  { category: 'format', text: "Use the same header as your resume (name, contact info) for visual consistency" },
  { category: 'format', text: "Standard font (11-12pt), 1-inch margins, left-aligned body text" },
  { category: 'format', text: "Save as PDF unless specifically asked for .doc format" },
];

// Cover letter checklist
const CHECKLIST = [
  "✓ Addressed to specific person (not 'To Whom It May Concern')",
  "✓ Company name is correct (no copy-paste errors!)",
  "✓ Job title matches the posting exactly",
  "✓ Opening hook captures attention in first sentence",
  "✓ At least one specific achievement with a number",
  "✓ Shows company research (why THIS company?)",
  "✓ Explains career transition positively (if applicable)",
  "✓ Clear call to action in closing",
  "✓ No typos or grammatical errors",
  "✓ One page maximum",
  "✓ Saved as PDF with professional filename (FirstName_LastName_CoverLetter.pdf)",
];

/**
 * Generate a complete cover letter package
 */
export function generateCoverLetter(
  career: CareerPath,
  userSegment: string | null = null
): CoverLetter {
  const segment = userSegment || 'general';

  // Get the appropriate opening based on segment
  const opening = (SEGMENT_OPENINGS[segment] || SEGMENT_OPENINGS.general)
    .replace(/\{TITLE\}/g, career.title)
    .replace(/\{CATEGORY\}/g, career.category.toLowerCase());

  // Get career-specific body paragraphs or generate defaults
  const bodyParagraphs = CAREER_SKILL_PARAGRAPHS[career.id] || [
    `My experience has given me strong skills in ${career.requiredSkills.slice(0, 3).join(', ')}, which I'm eager to apply to the ${career.title} role at [COMPANY].`,
    `What draws me to ${career.category.toLowerCase()} is the opportunity to [MOTIVATION]. I'm particularly excited about [COMPANY] because [SPECIFIC REASON].`,
  ];

  // Get career-specific closing or use default
  const closing = CAREER_CLOSINGS[career.id] ||
    `I'd welcome the opportunity to discuss how my skills can benefit [COMPANY]. Thank you for your consideration, and I look forward to hearing from you.`;

  // Build the complete template
  const template: CoverLetterTemplate = {
    greeting: "Dear [HIRING MANAGER NAME / Hiring Manager],",
    opening,
    body: bodyParagraphs,
    closing,
    signature: "[YOUR NAME]\n[PHONE NUMBER]\n[EMAIL ADDRESS]\n[LINKEDIN URL (optional)]",
  };

  // Build individual paragraphs with guidance
  const paragraphs: CoverLetterParagraph[] = [
    {
      id: 'opening',
      type: 'opening',
      title: 'Opening Hook',
      content: opening,
      tips: 'Your first sentence determines whether they keep reading. Lead with value, not "I saw your posting on Indeed."',
    },
    ...bodyParagraphs.map((content, idx) => ({
      id: `body-${idx}`,
      type: 'body' as const,
      title: idx === 0 ? 'Your Value Proposition' : 'Supporting Evidence',
      content,
      tips: idx === 0
        ? 'This paragraph should make them think "we need to interview this person."'
        : 'Back up your claims with specific examples or metrics.',
    })),
    {
      id: 'closing',
      type: 'closing',
      title: 'Closing & Call to Action',
      content: closing,
      tips: 'Be direct about wanting an interview. Don\'t end with a whimper.',
    },
  ];

  return {
    template,
    paragraphs,
    powerPhrases: POWER_PHRASES,
    avoidPhrases: AVOID_PHRASES.map(p => p.split(' → ')[0]), // Just the phrases
    tips: UNIVERSAL_TIPS,
    checklist: CHECKLIST,
  };
}

/**
 * Generate the full cover letter text for easy copying
 */
export function generateFullCoverLetterText(letter: CoverLetter): string {
  const { template } = letter;

  const fullText = `${template.greeting}

${template.opening}

${template.body.join('\n\n')}

${template.closing}

Sincerely,

${template.signature}`;

  return fullText;
}

/**
 * Get segment-specific opening paragraph variations
 */
export function getOpeningVariations(career: CareerPath): { segment: string; text: string }[] {
  return Object.entries(SEGMENT_OPENINGS).map(([segment, text]) => ({
    segment,
    text: text
      .replace(/\{TITLE\}/g, career.title)
      .replace(/\{CATEGORY\}/g, career.category.toLowerCase()),
  }));
}
